/**
 * Application header with navigation, status, and auth controls.
 * Responsive: hamburger menu on mobile (< md), horizontal layout on desktop.
 */

import { useState, useEffect, useRef } from 'react';
import type { BacktestResult } from '../../types';
import type { ActivePage } from '../../stores/paperTradingStore.js';
import { useAuthStore } from '../../stores/authStore.js';
import { AgentStatusIndicator } from './AgentStatusIndicator.js';
import { BrandLogo } from '../BrandLogo.js';

interface AppHeaderProps {
  activePage: ActivePage;
  onNavigate: (page: ActivePage) => void;
  currentResult: BacktestResult | null;
  onShowParams: () => void;
  onShowExplorer: () => void;
  onShowLogin?: () => void;
  isAuthenticated: boolean;
  username?: string;
  onLogout: () => void;
  hasParams: boolean;
  /** When false: dims Optimizations/Configurations/Trading and hides Explore Runs + Idle indicator. Defaults to true. */
  hasStrategies?: boolean;
}

/** Pages that require a strategy before they are useful. */
const STRATEGY_GATED_PAGES: ActivePage[] = ['optimizations', 'configurations', 'portfolios', 'paper-trading'];

const BASE_NAV_ITEMS: { label: string; page: ActivePage }[] = [
  { label: 'Backtesting', page: 'backtesting' },
  { label: 'Optimizations', page: 'optimizations' },
  { label: 'Strategies', page: 'configurations' },
  { label: 'Portfolios', page: 'portfolios' },
  { label: 'Trading', page: 'paper-trading' },
];

export function AppHeader({
  activePage,
  onNavigate,
  currentResult,
  onShowParams,
  onShowExplorer,
  onShowLogin: _onShowLogin,
  isAuthenticated,
  username,
  onLogout,
  hasParams,
  hasStrategies = true,
}: AppHeaderProps) {
  const authUser = useAuthStore((s) => s.user);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const NAV_ITEMS = isAuthenticated
    ? [
        ...BASE_NAV_ITEMS,
        { label: 'Settings', page: 'settings' as ActivePage },
        ...(authUser?.role === 'admin'
          ? [
              { label: 'Data Cache', page: 'admin-data' as ActivePage },
              { label: 'Activity', page: 'admin-activity' as ActivePage },
            ]
          : []),
      ]
    : BASE_NAV_ITEMS;

  // Close menu when clicking outside
  useEffect(() => {
    if (!menuOpen) return;
    function handleClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [menuOpen]);

  function isGated(page: ActivePage): boolean {
    return !hasStrategies && STRATEGY_GATED_PAGES.includes(page);
  }

  function handleNavigate(page: ActivePage) {
    if (isGated(page)) return;
    onNavigate(page);
    setMenuOpen(false);
  }

  return (
    <header className="border-b border-gray-800 bg-gray-900/55 backdrop-blur-xl px-4 py-3 flex-shrink-0 relative z-20" ref={menuRef}>
      <div className="flex items-center justify-between">
        {/* Left side: logo + desktop nav */}
        <div className="flex items-center gap-6">
          <BrandLogo markClassName="w-8 h-8 md:w-9 md:h-9" wordClassName="text-lg md:text-xl" />

          {/* Desktop page navigation — hidden on mobile */}
          <nav className="hidden md:flex items-center gap-1" data-testid="desktop-nav">
            {NAV_ITEMS.map(({ label, page }) => {
              const gated = isGated(page);
              return (
                <button
                  key={page}
                  data-page={page}
                  onClick={() => {
                    if (!gated) onNavigate(page);
                  }}
                  title={gated ? 'Create a strategy first' : undefined}
                  className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-all ${
                    gated
                      ? 'opacity-40 cursor-not-allowed text-gray-400'
                      : activePage === page
                        ? 'bg-primary-500/12 text-primary-200 ring-1 ring-inset ring-primary-400/30'
                        : 'text-gray-400 hover:text-gray-100 hover:bg-white/5'
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Right side */}
        <div className="flex items-center gap-2 md:gap-4">
          {/* Action buttons (backtesting page) — hidden on mobile, shown on desktop */}
          {activePage === 'backtesting' && (
            <div className="hidden md:flex items-center gap-4 text-sm">
              {currentResult && hasParams && (
                <button
                  onClick={onShowParams}
                  className="px-3 py-1.5 text-sm text-gray-300 hover:text-white bg-gray-800/60 hover:bg-gray-700/70 border border-gray-700 hover:border-gray-600 rounded-lg transition-colors flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                  Params
                </button>
              )}
              {hasStrategies && (
                <button
                  onClick={onShowExplorer}
                  className="px-3 py-1.5 text-sm text-gray-300 hover:text-white bg-gray-800/60 hover:bg-gray-700/70 border border-gray-700 hover:border-gray-600 rounded-lg transition-colors flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M4 6h16M4 10h16M4 14h16M4 18h16"
                    />
                  </svg>
                  Explore Runs
                </button>
              )}
            </div>
          )}

          {/* User info / logout — always visible when authenticated */}
          {isAuthenticated && (
            <div className="hidden md:flex items-center gap-3">
              {hasStrategies && <AgentStatusIndicator />}
              <span className="text-sm text-gray-400">{username}</span>
              <button
                onClick={onLogout}
                className="px-2 py-1 text-xs text-gray-400 hover:text-white bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded transition-colors"
              >
                Logout
              </button>
            </div>
          )}

          {/* Hamburger button — visible only on mobile */}
          <button
            className="flex md:hidden items-center justify-center w-9 h-9 rounded-lg text-gray-400 hover:text-white hover:bg-gray-800 transition-colors"
            onClick={() => setMenuOpen((prev) => !prev)}
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          >
            {menuOpen ? (
              /* X icon */
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              /* Hamburger icon */
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* Mobile dropdown menu */}
      {menuOpen && (
        <div
          data-testid="mobile-menu"
          className="md:hidden absolute top-full left-0 right-0 z-50 bg-gray-900/95 backdrop-blur-xl border-b border-gray-800 shadow-2xl"
        >
          {/* Navigation items */}
          <nav className="flex flex-col px-4 py-2 gap-1">
            {NAV_ITEMS.map(({ label, page }) => {
              const gated = isGated(page);
              return (
                <button
                  key={page}
                  data-page={page}
                  onClick={() => {
                    if (!gated) {
                      handleNavigate(page);
                    }
                  }}
                  title={gated ? 'Create a strategy first' : undefined}
                  className={`w-full text-left px-3 py-2.5 text-sm font-medium rounded-lg transition-all ${
                    gated
                      ? 'opacity-40 cursor-not-allowed text-gray-400'
                      : activePage === page
                        ? 'bg-primary-500/15 text-primary-200 ring-1 ring-inset ring-primary-400/30'
                        : 'text-gray-300 hover:text-white hover:bg-white/5'
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </nav>

          {/* Divider */}
          <div className="border-t border-gray-700 mx-4" />

          {/* Auth section — logout only (unauthenticated users never see the header) */}
          {isAuthenticated && (
            <div className="px-4 py-3 flex items-center justify-between">
              <span className="text-sm text-gray-400">{username}</span>
              <button
                onClick={() => { onLogout(); setMenuOpen(false); }}
                className="px-3 py-1.5 text-xs text-gray-400 hover:text-white bg-gray-700 hover:bg-gray-600 border border-gray-600 rounded transition-colors"
              >
                Logout
              </button>
            </div>
          )}

          {/* Backtesting action buttons in mobile menu */}
          {activePage === 'backtesting' && (
            <>
              <div className="border-t border-gray-700 mx-4" />
              <div className="px-4 py-3 flex flex-col gap-2">
                {currentResult && hasParams && (
                  <button
                    onClick={() => { onShowParams(); setMenuOpen(false); }}
                    className="w-full px-3 py-2.5 text-sm text-gray-300 hover:text-white bg-gray-700 hover:bg-gray-600 border border-gray-600 rounded-lg transition-colors flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                    Params
                  </button>
                )}
                {hasStrategies && (
                  <button
                    onClick={() => { onShowExplorer(); setMenuOpen(false); }}
                    className="w-full px-3 py-2.5 text-sm text-gray-300 hover:text-white bg-gray-700 hover:bg-gray-600 border border-gray-600 rounded-lg transition-colors flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M4 6h16M4 10h16M4 14h16M4 18h16"
                      />
                    </svg>
                    Explore Runs
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </header>
  );
}
